import { autorun, observable, runInAction } from 'mobx';
import API from '../util/ApiUtil';
import RootStore from './RootStore';
import LanguageStore, { Language } from './LanguageStore';
import { AxiosResponse } from 'axios';
import ErrorUtil from '../util/ErrorUtil';

export interface NewsParams {
  offset?: number;
  perPage: number;
  tags?: string;
  categories?: string;
}

// tslint:disable-next-line:interface-name
export interface INewsItem {
  id: string;
  title: string;
  slug: string;
  isoDate?: string;
  displayDate?: string;
  url?: string;
  date?: string;
  button?: {};
  link?: string;
  excerpt?: string;
}

export interface NewsList {
  items: INewsItem[];
  status?: string;
  total?: number;
}

interface QueryParams {
  offset?: number;
  perPage: number;
}

const THIRTY_SECONDS = 30 * 1000;

class NewsStore {
  @observable accessor status: Status = 'UNINITIALIZED';
  @observable accessor news: NewsList = { total: 0, items: [] };

  private languageStore: LanguageStore;
  private params: NewsParams;
  private lang: Language;
  private app: App;
  private disposeLiveUpdate: () => void;

  public constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.app = rootStore.app;
  }

  public subscribe = (params: NewsParams = { offset: 0, perPage: 10 }) => {
    if (!this.disposeLiveUpdate) {
      this.disposeLiveUpdate = autorun(() => this.reloadNews(this.languageStore.currentLanguage, params));
    }
  };

  public unsubscribe = () => {
    if (this.disposeLiveUpdate) {
      this.disposeLiveUpdate();
      delete this.disposeLiveUpdate;
    }
  };

  public fetchNotRequired = (lang: Language, params: NewsParams) => {
    return lang === this.lang && params.perPage <= this.news.items.length && !params.tags && !params.categories;
  };

  public allFetched = () => {
    return this.news.items.length === this.news.total;
  };

  public allShown = (count: number) => {
    return this.news.total <= count;
  };

  public reloadNews = (lang: Language = 'et', params: NewsParams): Promise<NewsList | void> => {
    if (this.fetchNotRequired(lang, params)) {
      runInAction(() => (this.status = 'FETCHED'));
      return Promise.resolve();
    }
    this.params = params;
    runInAction(() => (this.status = 'FETCHING'));
    return this.fetchNews(lang, params).then((response: AxiosResponse<NewsList>) => {
      runInAction(() => {
        this.news = response.data;
        this.status = 'FETCHED';
      });
      this.lang = lang;
    });
  };

  public fetchMore = (totalCount: number): Promise<NewsList | void> => {
    const itemLength = this.news.items.length;
    if (this.allFetched() || totalCount < itemLength) {
      return Promise.resolve();
    }
    runInAction(() => (this.status = 'FETCHING'));
    const offset = (this.params.offset || 0) + itemLength;
    const perPage = totalCount - itemLength;
    const params = { ...this.params, offset, perPage };
    return this.fetchNews(this.lang, params).then(({ data: { total, items } }) => {
      runInAction(() => {
        this.news.total = total;
        this.news.items = this.news.items.concat(items);
        this.status = 'FETCHED';
      });
    });
  };

  public fetchNews = async (lang: Language, queryParams: QueryParams, withContent: boolean = false): Promise<AxiosResponse> => {
    const params = withContent ? queryParams : { context: 'embed', ...queryParams };
    try {
      return await API.get(`/news/${lang}`, { params: { ...params, app: this.app }, timeout: THIRTY_SECONDS });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.status = 'NOT_FOUND';
    }
  };

  public newsItems = (count: number) => {
    return this.news.items.slice(0, count);
  };

  public notFound = () => {
    return this.status === 'NOT_FOUND';
  };

  public isFetching = () => {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.status);
  };
}

export default NewsStore;
